(() => {
  const initializedSections = new WeakMap();
  const retryCounts = new WeakMap();
  const MAX_RETRIES = 80;

  const toBoolean = (value) => value === 'true';
  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const updateActiveItem = (section, activeIndex) => {
    section.querySelectorAll('[data-editorially-loved-item]').forEach((item) => {
      const slide = item.closest('.editorially-loved__slide');
      const slideIndex = Number(slide?.dataset.editoriallyLovedIndex);
      const isClone = slide?.classList.contains('splide__slide--clone');
      item.setAttribute('aria-pressed', !isClone && slideIndex === activeIndex ? 'true' : 'false');
      item.setAttribute('tabindex', isClone ? '-1' : '0');
    });
  };

  const initializeSection = (section) => {
    if (!section || initializedSections.has(section)) return;

    if (typeof window.Splide !== 'function') {
      const retries = retryCounts.get(section) || 0;
      if (retries < MAX_RETRIES) {
        retryCounts.set(section, retries + 1);
        window.setTimeout(() => initializeSection(section), 100);
      }
      return;
    }

    const slider = section.querySelector('[data-editorially-loved-slider]');
    const slides = Array.from(section.querySelectorAll('.editorially-loved__slide'));
    if (!slider || slides.length === 0) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const splide = new window.Splide(slider, {
      type: slides.length > 1 ? 'loop' : 'slide',
      arrows: false,
      pagination: slides.length > 1,
      autoplay: !reduceMotion && toBoolean(section.dataset.autoplay),
      interval: toNumber(section.dataset.autoplaySpeed, 5000),
      speed: reduceMotion ? 0 : toNumber(section.dataset.transitionSpeed, 600),
      pauseOnHover: toBoolean(section.dataset.pauseOnHover),
      pauseOnFocus: true,
      resetProgress: false,
      perMove: 1,
      perPage: 3,
      focus: 0,
      gap: '80px',
      mediaQuery: 'max',
      breakpoints: {
        999: {
          perPage: 2,
          gap: '48px'
        },
        699: {
          perPage: 1,
          gap: '16px'
        }
      }
    });

    const syncActiveState = () => updateActiveItem(section, splide.index);
    splide.on('mounted moved active', syncActiveState);
    splide.mount();

    section.querySelectorAll('[data-editorially-loved-item]').forEach((item) => {
      const index = Number(item.closest('.editorially-loved__slide')?.dataset.editoriallyLovedIndex);
      const activate = () => {
        if (Number.isInteger(index)) splide.go(index);
      };

      item.addEventListener('click', activate);
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate();
        }
      });
    });

    initializedSections.set(section, splide);
    retryCounts.delete(section);
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.('[data-editorially-loved]')) initializeSection(scope);
    scope.querySelectorAll?.('[data-editorially-loved]').forEach(initializeSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.('[data-editorially-loved]')
      ? event.target
      : event.target.querySelector?.('[data-editorially-loved]');
    const splide = section && initializedSections.get(section);

    if (splide) {
      splide.destroy(true);
      initializedSections.delete(section);
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest?.('[data-editorially-loved]');
    const splide = section && initializedSections.get(section);
    const slide = event.target.closest?.('.editorially-loved__slide');

    if (!splide || !slide) return;
    const slides = Array.from(section.querySelectorAll('.editorially-loved__slide:not(.splide__slide--clone)'));
    const index = slides.indexOf(slide);
    if (index >= 0) splide.go(index);
  });
})();
